import React, { useState, useEffect } from 'react';
import { X, MapPin, Tag, Info, Sparkles, Loader2, AlertTriangle, CheckCircle2, Edit2, Save, Search, Briefcase, Users as UsersIcon, TrendingUp } from 'lucide-react';
import { useDetectOfertas } from '../hooks/useDetectOfertas';
import { useOptions } from '../contexts/OptionsContext';
import { useAuth } from '../hooks/useAuth';
import { getStageColor, colaboradores } from '../utils/stageHelpers';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return '—'; }
};

const getConfiancaStyle = (c) => {
  switch ((c || '').toLowerCase()) {
    case 'alta':  return { bg: 'bg-green-100', text: 'text-green-800', label: 'Alta' };
    case 'media': return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Média' };
    case 'baixa': return { bg: 'bg-red-100', text: 'text-red-800', label: 'Baixa' };
    default:      return { bg: 'bg-slate-100', text: 'text-slate-700', label: c || '—' };
  }
};

const SugestaoRow = ({ sugestao, selected, onToggle, onEstagioChange, dealStages }) => {
  const conf = getConfiancaStyle(sugestao.confianca);
  const [showEvidencias, setShowEvidencias] = useState(false);

  return (
    <div className={`border rounded-md p-3 transition-colors ${selected ? 'border-purple-400 bg-purple-50/50' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="mt-1 w-4 h-4 accent-purple-600 flex-shrink-0"
        />

        <div className="flex-grow min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <Briefcase size={14} className="text-orange-600 flex-shrink-0" />
              <span className="font-medium text-sm text-slate-800">
                {sugestao.empresa_nome}
              </span>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${conf.bg} ${conf.text} flex-shrink-0`}>
                {conf.label}
              </span>
              {sugestao.palavras_distintas_match != null && (
                <span className="text-[10px] text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded flex items-center gap-0.5" title="Quantas palavras-chave distintas bateram">
                  <TrendingUp size={9} /> {sugestao.palavras_distintas_match} palavra(s)
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-slate-600 mb-2">
            <div>
              <span className="text-slate-400">Primeira abordagem:</span>{' '}
              <span className="font-medium">{formatDate(sugestao.primeira_abordagem)}</span>
            </div>
            <div>
              <span className="text-slate-400">Última interação:</span>{' '}
              <span className="font-medium">{formatDate(sugestao.ultima_interacao)}</span>
            </div>
            <div>
              <span className="text-slate-400">Emails:</span>{' '}
              <span className="font-medium">{sugestao.emails_analisados}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-[11px] text-slate-500 whitespace-nowrap">Estágio:</span>
            <select
              value={sugestao.estagio_sugerido || ''}
              onChange={(e) => onEstagioChange(e.target.value)}
              className={`text-xs px-2 py-1 rounded border border-slate-300 ${getStageColor(sugestao.estagio_sugerido)}`}
            >
              <option value="">— Selecione —</option>
              {(dealStages || []).map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            {(sugestao.confianca === 'media' || sugestao.confianca === 'baixa') && (
              <span className="text-[10px] text-amber-600 flex items-center gap-0.5">
                <AlertTriangle size={10} /> revisar
              </span>
            )}
          </div>

          {sugestao.contatos_envolvidos && sugestao.contatos_envolvidos.length > 0 && (
            <div className="text-[11px] text-slate-600 mb-1">
              <UsersIcon size={10} className="inline mr-1" />
              {sugestao.contatos_envolvidos.slice(0, 3).join(', ')}
              {sugestao.contatos_envolvidos.length > 3 && ` +${sugestao.contatos_envolvidos.length - 3}`}
            </div>
          )}

          {sugestao.evidencias && sugestao.evidencias.length > 0 && (
            <button
              onClick={() => setShowEvidencias(!showEvidencias)}
              className="text-[11px] text-purple-600 hover:text-purple-800"
            >
              {showEvidencias ? '− ocultar evidências' : `+ ver ${sugestao.evidencias.length} evidência(s)`}
            </button>
          )}
          {showEvidencias && (
            <ul className="mt-1 space-y-0.5 pl-3 border-l-2 border-purple-200">
              {sugestao.evidencias.map((e, i) => (
                <li key={i} className="text-[11px] text-slate-600 italic">"{e}"</li>
              ))}
            </ul>
          )}

          {sugestao.observacoes && (
            <div className="mt-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              <Info size={10} className="inline mr-1" />
              {sugestao.observacoes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AreaDetailModal = ({ area, onClose, onUpdateArea, onDetectSuccess }) => {
  const { dealStages } = useOptions();
  const { user } = useAuth();
  const { loading: detecting, error: detectError, result: detectResult, detectar, limpar, criarOfertasEmLote } = useDetectOfertas();

  const [editMode, setEditMode] = useState(false);
  const [editNome, setEditNome] = useState(area.nome || '');
  const [editDescricao, setEditDescricao] = useState(area.descricao || '');
  const [editPalavras, setEditPalavras] = useState(area.palavras_chave || []);
  const [inputPalavra, setInputPalavra] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [sugestoesState, setSugestoesState] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [responsavelParaOfertas, setResponsavelParaOfertas] = useState(user?.email?.split('@')[0] || 'eduardo');
  const [creatingOfertas, setCreatingOfertas] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  useEffect(() => {
    if (detectResult?.sugestoes) {
      setSugestoesState(detectResult.sugestoes.map(s => ({ ...s })));
      const ids = new Set(
        detectResult.sugestoes
          .filter(s => s.confianca === 'alta' || s.confianca === 'media')
          .map(s => s.empresa_id)
      );
      setSelectedIds(ids);
    }
  }, [detectResult]);

  const adicionarPalavra = () => {
    const termo = inputPalavra.trim();
    if (!termo || termo.length < 2 || editPalavras.includes(termo)) {
      setInputPalavra('');
      return;
    }
    setEditPalavras([...editPalavras, termo]);
    setInputPalavra('');
  };

  const handleSaveEdit = async () => {
    if (!editNome.trim()) return;
    setSavingEdit(true);
    try {
      const palavrasFinais = inputPalavra.trim()
        ? [...editPalavras, inputPalavra.trim()]
        : editPalavras;
      await onUpdateArea(area.id, {
        nome: editNome.trim(),
        descricao: editDescricao.trim() || null,
        palavras_chave: palavrasFinais,
      });
      setEditMode(false);
      setInputPalavra('');
    } catch (err) {
      alert('Erro ao salvar: ' + err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDetect = async () => {
    limpar();
    setSuccessMsg(null);
    await detectar(area.id);
  };

  const toggleSelected = (empresaId) => {
    const ns = new Set(selectedIds);
    if (ns.has(empresaId)) ns.delete(empresaId); else ns.add(empresaId);
    setSelectedIds(ns);
  };

  const toggleAll = () => {
    if (selectedIds.size === sugestoesState.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(sugestoesState.map(s => s.empresa_id)));
  };

  const updateEstagioSugestao = (empresaId, novoEstagio) => {
    setSugestoesState(prev =>
      prev.map(s => s.empresa_id === empresaId ? { ...s, estagio_sugerido: novoEstagio } : s)
    );
  };

  const handleCriarOfertas = async () => {
    const aprovadas = sugestoesState.filter(s => selectedIds.has(s.empresa_id));
    if (aprovadas.length === 0) return;

    setCreatingOfertas(true);
    setSuccessMsg(null);
    try {
      const { count } = await criarOfertasEmLote(area.id, area.nome, aprovadas, responsavelParaOfertas);
      setSuccessMsg(`${count} oferta(s) criada(s) com sucesso!`);
      setSugestoesState(prev => prev.filter(s => !selectedIds.has(s.empresa_id)));
      setSelectedIds(new Set());
      if (onDetectSuccess) onDetectSuccess();
    } catch (err) {
      alert('Erro ao criar ofertas: ' + err.message);
    } finally {
      setCreatingOfertas(false);
    }
  };

  const palavrasChaveAtual = area.palavras_chave || [];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] 2xl:max-w-7xl max-h-[95vh] flex flex-col">

        <div className="p-5 border-b border-slate-200 flex-shrink-0">
          <div className="flex justify-between items-start gap-3">
            <div className="flex items-start gap-3 min-w-0 flex-grow">
              <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0">
                <MapPin className="text-purple-600" size={22} />
              </div>
              <div className="min-w-0 flex-grow">
                {editMode ? (
                  <input
                    type="text"
                    value={editNome}
                    onChange={(e) => setEditNome(e.target.value)}
                    className="w-full text-lg font-bold border border-purple-300 rounded px-2 py-1"
                    autoFocus
                  />
                ) : (
                  <h2 className="text-xl font-bold text-slate-800 truncate">{area.nome}</h2>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {!editMode ? (
                <button
                  onClick={() => setEditMode(true)}
                  className="p-1.5 text-slate-600 hover:bg-slate-100 rounded"
                  title="Editar área"
                >
                  <Edit2 size={16} />
                </button>
              ) : (
                <button
                  onClick={handleSaveEdit}
                  disabled={!editNome.trim() || savingEdit}
                  className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm flex items-center gap-1"
                >
                  <Save size={14} /> {savingEdit ? 'Salvando…' : 'Salvar'}
                </button>
              )}
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                <X size={22} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-grow overflow-y-auto">
          <section className="p-5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Informações</h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Descrição</label>
                {editMode ? (
                  <textarea
                    value={editDescricao}
                    onChange={(e) => setEditDescricao(e.target.value)}
                    rows={2}
                    className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 resize-none"
                    placeholder="Opcional"
                  />
                ) : (
                  <p className="text-sm text-slate-700">
                    {area.descricao || <span className="italic text-slate-400">sem descrição</span>}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  <Tag size={11} className="inline mr-1" /> Palavras-chave
                  <span className="text-slate-400 ml-2 font-normal">(usadas pela IA para buscar nos emails)</span>
                </label>

                {editMode ? (
                  <>
                    {editPalavras.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {editPalavras.map((p, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-xs">
                            {p}
                            <button onClick={() => setEditPalavras(editPalavras.filter((_, idx) => idx !== i))}>
                              <X size={10} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={inputPalavra}
                        onChange={(e) => setInputPalavra(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            adicionarPalavra();
                          }
                        }}
                        className="flex-grow px-2 py-1 border border-slate-300 rounded text-sm"
                        placeholder="Nova palavra-chave (Enter para adicionar)"
                      />
                      <button
                        onClick={adicionarPalavra}
                        className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200"
                      >
                        + Adicionar
                      </button>
                    </div>
                  </>
                ) : (
                  palavrasChaveAtual.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {palavrasChaveAtual.map((p, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-xs">
                          {p}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-center gap-1.5">
                      <AlertTriangle size={12} />
                      Nenhuma palavra-chave cadastrada. Edite a área para adicionar antes de detectar ofertas.
                    </div>
                  )
                )}
              </div>
            </div>
          </section>

          <section className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                <Sparkles size={14} className="text-purple-600" />
                Detecção de ofertas nos emails
              </h3>
              <button
                onClick={handleDetect}
                disabled={detecting || palavrasChaveAtual.length === 0}
                className="px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-1.5 shadow-sm"
                title={palavrasChaveAtual.length === 0 ? 'Adicione palavras-chave primeiro' : 'Analisar emails via IA'}
              >
                {detecting ? (
                  <><Loader2 size={14} className="animate-spin" /> Analisando…</>
                ) : (
                  <><Search size={14} /> Detectar ofertas</>
                )}
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-2.5 text-xs text-blue-800 mb-3">
              <Info size={12} className="inline mr-1" />
              A IA busca menções desta área nos emails e analisa até <strong>50 empresas mais relevantes</strong> por execução (ordenadas por palavras-chave distintas matched). Empresas que recebem a oferta sem resposta entram como <strong>7.1.Sem_Resposta</strong>. Você aprova ou rejeita cada sugestão antes de criar as ofertas.
            </div>

            {detectError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800 mb-3 flex items-start gap-2">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <div className="flex-grow">
                  <strong>Erro:</strong> {detectError}
                </div>
              </div>
            )}

            {successMsg && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-800 mb-3 flex items-start gap-2">
                <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" />
                <div className="flex-grow">{successMsg}</div>
              </div>
            )}

            {detectResult && !detecting && (
              <>
                {detectResult.avisos && detectResult.avisos.length > 0 && (
                  <div className="mb-3 space-y-1.5">
                    {detectResult.avisos.map((aviso, i) => (
                      <div key={i} className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-800 flex items-start gap-2">
                        <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                        <span>{aviso}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="bg-slate-50 border border-slate-200 rounded p-2.5 mb-3 text-xs text-slate-600 grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div><strong>{detectResult.total_emails_analisados}</strong> emails matched</div>
                  <div>
                    <strong>{detectResult.empresas_analisadas}</strong> / {detectResult.empresas_candidatas || detectResult.empresas_analisadas} empresas analisadas
                  </div>
                  <div><strong>{detectResult.empresas_ja_com_oferta}</strong> já têm oferta</div>
                  {detectResult.metadata?.custo_estimado_usd != null && (
                    <div>custo: <strong>${detectResult.metadata.custo_estimado_usd.toFixed(3)}</strong></div>
                  )}
                </div>

                {sugestoesState.length === 0 ? (
                  <div className="text-center py-8 text-sm text-slate-500">
                    Nenhuma oferta nova detectada.
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={toggleAll}
                          className="text-xs text-purple-600 hover:text-purple-800"
                        >
                          {selectedIds.size === sugestoesState.length ? 'Desmarcar todas' : 'Selecionar todas'}
                        </button>
                        <span className="text-xs text-slate-500">
                          {selectedIds.size} / {sugestoesState.length} selecionada(s)
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-600">Responsável:</label>
                        <select
                          value={responsavelParaOfertas}
                          onChange={(e) => setResponsavelParaOfertas(e.target.value)}
                          className="text-xs border border-slate-300 rounded px-2 py-1"
                        >
                          {colaboradores.filter(c => c.id !== 'all').map(c => (
                            <option key={c.id} value={c.id}>{c.nome}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {sugestoesState.map(s => (
                        <SugestaoRow
                          key={s.empresa_id}
                          sugestao={s}
                          selected={selectedIds.has(s.empresa_id)}
                          onToggle={() => toggleSelected(s.empresa_id)}
                          onEstagioChange={(v) => updateEstagioSugestao(s.empresa_id, v)}
                          dealStages={dealStages}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </section>
        </div>

        {sugestoesState.length > 0 && (
          <div className="p-4 border-t border-slate-200 bg-slate-50 flex-shrink-0 flex justify-between items-center">
            <div className="text-xs text-slate-600">
              {selectedIds.size > 0
                ? `Pronto para criar ${selectedIds.size} oferta(s)`
                : 'Selecione sugestões para criar ofertas'}
            </div>
            <button
              onClick={handleCriarOfertas}
              disabled={selectedIds.size === 0 || creatingOfertas}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-1.5 shadow-sm"
            >
              {creatingOfertas ? (
                <><Loader2 size={14} className="animate-spin" /> Criando…</>
              ) : (
                <><Briefcase size={14} /> Criar {selectedIds.size} oferta(s)</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AreaDetailModal;
